import {
 useState, useEffect, useCallback, useMemo 
} from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Box,
  Collapse,
  Typography,
} from '@mui/material';
import { usePremiumTokens } from '../common/theme/usePremiumTokens';
import GpsFixedIcon from '@mui/icons-material/GpsFixed';
import LocationSearchingIcon from '@mui/icons-material/LocationSearching';
import PauseCircleIcon from '@mui/icons-material/PauseCircle';
import TimerIcon from '@mui/icons-material/Timer';
import AvTimerIcon from '@mui/icons-material/AvTimer';
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation';
import WarningIcon from '@mui/icons-material/Warning';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import TimelineIcon from '@mui/icons-material/Timeline';
import { StatCard, StatCardGrid } from '../common/components/premium/PremiumDashboard';
import { PremiumBarChart } from '../common/components/premium/PremiumCharts';
import {
  formatDistance,
  formatVolume,
  formatTime,
  formatNumericHours,
} from '../common/util/formatter';
import ReportFilter from './components/ReportFilter';
import { useAttributePreference } from '../common/util/preferences';
import { useTranslation } from '../common/components/LocalizationProvider';
import PageLayout from '../common/components/PageLayout';
import ReportsMenu from './components/ReportsMenu';
import ColumnSelect from './components/ColumnSelect';
import usePersistedState from '../common/util/usePersistedState';
import { useCatch } from '../reactHelper';
import useReportStyles from './common/useReportStyles';
import MapPositions from '../map/MapPositions';
import MapView from '../map/core/MapView';
import MapCamera from '../map/MapCamera';
import PoiLocationValue from '../common/components/PoiLocationValue';
import TableShimmer from '../common/components/TableShimmer';
import MapGeofence from '../map/MapGeofence';
import scheduleReport from './common/scheduleReport';
import MapScale from '../map/MapScale';
import fetchOrThrow from '../common/util/fetchOrThrow';
import { nativePostMessage } from '../common/components/NativeInterface';
import { sessionActions } from '../store';
import { PrintLayout } from './common/print';
import StatusCard from '../common/components/StatusCard';
import downloadReportXlsx from '../common/util/downloadReportXlsx';
import { deviceEquality } from '../common/util/deviceEquality';
// 2ECOMP: Mini-map components
import { MiniMapCell, MapExpandModal } from '../common/components/premium';

const columnsArray = [
  ['startTime', 'reportStartTime'],
  ['startOdometer', 'positionOdometer'],
  ['address', 'positionAddress'],
  ['endTime', 'reportEndTime'],
  ['duration', 'reportDuration'],
  ['engineHours', 'reportEngineHours'],
  ['spentFuel', 'reportSpentFuel'],
];
const columnsMap = new Map(columnsArray);

const StopReportPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { classes } = useReportStyles();
  const t = useTranslation();
  const tokens = usePremiumTokens();

  const devices = useSelector((state) => state.devices.items, deviceEquality(['id', 'name']));
  const printing = useSelector((state) => state.session.printing ?? false);

  const distanceUnit = useAttributePreference('distanceUnit');
  const volumeUnit = useAttributePreference('volumeUnit');
  const [columns, setColumns] = usePersistedState(
    'stopColumns',
    ['startTime', 'endTime', 'startOdometer', 'address'],
  );
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [showCard, setShowCard] = useState(false);
  const [hasResults, setHasResults] = useState(false);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [selectedDeviceIds, setSelectedDeviceIds] = useState([]);
  const [selectedGroupIds, setSelectedGroupIds] = useState([]);
  const [showChart, setShowChart] = useState(true);
  // 2ECOMP: Expanded map state
  const [expandedMap, setExpandedMap] = useState(null);

  // Stats totais
  const stats = useMemo(() => {
    if (items.length === 0) return null;
    const totalDuration = items.reduce((acc, item) => acc + (item.duration || 0), 0);
    const totalEngineHours = items.reduce((acc, item) => acc + (item.engineHours || 0), 0);
    const totalFuel = items.reduce((acc, item) => acc + (item.spentFuel || 0), 0);
    const avgDuration = totalDuration / items.length;
    const longStops = items.filter((item) => (item.duration || 0) > 3600000).length; // > 1 hour

    return {
      stopCount: items.length,
      totalDuration: (totalDuration / 3600000).toFixed(1), // hours
      avgDuration: (avgDuration / 60000).toFixed(0), // minutes
      totalEngineHours: (totalEngineHours / 3600000).toFixed(1),
      totalFuel: totalFuel.toFixed(2),
      longStops,
      uniqueLocations: new Set(items.map((i) => `${i.latitude?.toFixed(3)},${i.longitude?.toFixed(3)}`)).size,
    };
  }, [items]);

  // Dados para o gráfico de paradas por dia
  const chartData = useMemo(() => {
    if (items.length === 0) return [];
    const byDate = {};
    items.forEach((item) => {
      const date = formatTime(item.startTime, 'date');
      if (!byDate[date]) {
        byDate[date] = { date, stops: 0, duration: 0 };
      }
      byDate[date].stops += 1;
      byDate[date].duration += (item.duration || 0) / 60000; // minutes
    });
    return Object.values(byDate).slice(-14);
  }, [items]);

  useEffect(() => {
    const handleAfterPrint = () => dispatch(sessionActions.setPrinting(false));
    window.addEventListener('afterprint', handleAfterPrint);
    return () => {
      window.removeEventListener('afterprint', handleAfterPrint);
    };
  }, [dispatch]);

  useEffect(() => {
    if (printing) {
      requestAnimationFrame(() =>
        requestAnimationFrame(() => nativePostMessage(`print|${t('reportStops')}.pdf`)),
      );
    }
  }, [printing, t]);

  const loadStops = async ({ deviceIds = [], groupIds = [], from, to }) => {
    const query = new URLSearchParams({ from, to });
    deviceIds.forEach((deviceId) => query.append('deviceId', deviceId));
    groupIds.forEach((groupId) => query.append('groupId', groupId));
    setLoading(true);
    try {
      const response = await fetchOrThrow(`/api/reports/stops?${query.toString()}`, {
        headers: { Accept: 'application/json' },
      });
      const data = await response.json();
      setItems(data);
      setHasResults(data.length > 0);
    } finally {
      setLoading(false);
    }
  };

  const onShow = useCatch(async ({ deviceIds, groupIds, from, to }) => {
    setSelectedDeviceIds(deviceIds);
    setSelectedGroupIds(groupIds);
    setFromDate(from || '');
    setToDate(to || '');
    setSelectedItem(null);
    setShowCard(false);
    await loadStops({ deviceIds, groupIds, from, to });
  });

  const onExport = useCatch(async ({ deviceIds, groupIds, from, to }) => {
    const query = new URLSearchParams({ from, to });
    deviceIds.forEach((id) => query.append('deviceId', id));
    groupIds.forEach((id) => query.append('groupId', id));
    await downloadReportXlsx('stops', query, `${t('reportStops')}.xlsx`);
  });

  const onSchedule = useCatch(async (deviceIds, groupIds, report) => {
    report.type = 'stops';
    await scheduleReport(deviceIds, groupIds, report);
    navigate('/reports/scheduled');
  });

  const onPrint = useCatch(async ({ deviceIds = [], groupIds = [], from, to } = {}) => {
    const effectiveDeviceIds = deviceIds.length ? deviceIds : selectedDeviceIds;
    const effectiveGroupIds = groupIds.length ? groupIds : selectedGroupIds;
    const effectiveFrom = from ?? fromDate;
    const effectiveTo = to ?? toDate;

    if (!effectiveDeviceIds.length || !effectiveFrom || !effectiveTo) {
      return;
    }

    if (!items.length) {
      await loadStops({
        deviceIds: effectiveDeviceIds,
        groupIds: effectiveGroupIds,
        from: effectiveFrom,
        to: effectiveTo,
      });
    }

    setSelectedDeviceIds(effectiveDeviceIds);
    setSelectedGroupIds(effectiveGroupIds);
    setFromDate(effectiveFrom || '');
    setToDate(effectiveTo || '');
    dispatch(sessionActions.setPrinting(true));
  });

  const formatValue = (item, key) => {
    const value = item[key];
    switch (key) {
      case 'deviceId':
        return devices[value]?.name || value;
      case 'startTime':
      case 'endTime':
        return formatTime(value, 'minutes');
      case 'startOdometer':
        return formatDistance(value, distanceUnit, t);
      case 'duration':
        return formatNumericHours(value, t);
      case 'engineHours':
        return value > 0 ? formatNumericHours(value, t) : null;
      case 'spentFuel':
        return value > 0 ? formatVolume(value, volumeUnit, t) : null;
      case 'address':
        return (
          <PoiLocationValue
            latitude={item.latitude}
            longitude={item.longitude}
            poiId={item.poiId}
            poiName={item.poiName}
            originalAddress={value}
          />
        );
      default:
        return value;
    }
  };

  const getPrintableValue = (item, key) => {
    const value = item[key];
    switch (key) {
      case 'deviceId':
        return devices[value]?.name || `${value}`;
      case 'startTime':
      case 'endTime':
        return formatTime(value, 'minutes');
      case 'startOdometer':
        return formatDistance(value, distanceUnit, t) || '';
      case 'duration':
        return formatNumericHours(value, t) || '';
      case 'engineHours':
        return value > 0 ? formatNumericHours(value, t) : '';
      case 'spentFuel':
        return value > 0 ? formatVolume(value, volumeUnit, t) : '';
      case 'address':
        return item.address || '';
      default:
        return value != null ? `${value}` : '';
    }
  };

  const onMarkerClick = useCallback(() => {
    setShowCard(true);
  }, []);

  const deviceNames = selectedDeviceIds
    .map((deviceId) => devices[deviceId]?.name || devices[deviceId]?.uniqueId || `${deviceId}`)
    .filter(Boolean)
    .join(', ');

  if (printing) {
    return (
      <PrintLayout
        title={t('reportStops')}
        from={fromDate}
        to={toDate}
        deviceName={deviceNames}
        onReady={() => window.print()}
      >
        <Table className="print-table">
          <TableHead>
            <TableRow>
              <TableCell>{t('sharedDevice')}</TableCell>
              {/* 2ECOMP: Mini-map column for print */}
              <TableCell sx={{ width: 70, textAlign: 'center' }}>{t('positionMap') || 'Mapa'}</TableCell>
              {columns.map((key) => (
                <TableCell key={key}>{t(columnsMap.get(key))}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((item) => (
              <TableRow key={`print-${item.id}`}>
                <TableCell>{devices[item.deviceId]?.name || item.deviceId}</TableCell>
                {/* 2ECOMP: Mini-map cell for print */}
                <TableCell sx={{ textAlign: 'center', py: 0.5 }}>
                  <MiniMapCell
                    latitude={item.latitude}
                    longitude={item.longitude}
                    size="xs"
                    markerIcon="stop"
                    showExpandButton={false}
                    forPrint
                  />
                </TableCell>
                {columns.map((key) => (
                  <TableCell key={key}>{getPrintableValue(item, key)}</TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </PrintLayout>
    );
  }

  return (
    <PageLayout menu={<ReportsMenu />} breadcrumbs={['reportTitle', 'reportStops']}>
      <div className={classes.container}>
        {selectedItem && (
          <div className={classes.containerMap}>
            <MapView>
              <MapGeofence />
              <MapPositions
                positions={[{
                  id: selectedItem.positionId || selectedItem.id,
                  deviceId: selectedItem.deviceId,
                  fixTime: selectedItem.startTime,
                  latitude: selectedItem.latitude,
                  longitude: selectedItem.longitude,
                  course: selectedItem.course || 0,
                  attributes: selectedItem.attributes || {},
                }]}
                titleField="fixTime"
                onMarkerClick={onMarkerClick}
              />
              {showCard && (
                <StatusCard
                  deviceId={selectedItem.deviceId}
                  position={{
                    id: selectedItem.positionId || selectedItem.id,
                    deviceId: selectedItem.deviceId,
                    fixTime: selectedItem.startTime,
                    address: selectedItem.address,
                    latitude: selectedItem.latitude,
                    longitude: selectedItem.longitude,
                    course: selectedItem.course || 0,
                    attributes: selectedItem.attributes || {},
                    speed: selectedItem.speed,
                    totalDistance: selectedItem.totalDistance || selectedItem.distance,
                  }}
                  onClose={() => setShowCard(false)}
                  disableActions
                />
              )}
            </MapView>
            <MapScale />
            <MapCamera latitude={selectedItem.latitude} longitude={selectedItem.longitude} />
          </div>
        )}
        <div className={classes.containerMain}>
          <div className={classes.header}>
            <ReportFilter
              onShow={onShow}
              onExport={onExport}
              onPrint={onPrint}
              onSchedule={onSchedule}
              deviceType="multiple"
              loading={loading}
              hasResults={hasResults}
            >
              <ColumnSelect columns={columns} setColumns={setColumns} columnsArray={columnsArray} />
            </ReportFilter>
          </div>
          {/* Premium Stats Cards */}
          {stats && (
            <Box sx={{ px: 2, py: 2, borderTop: `1px solid ${tokens?.border?.subtle || 'rgba(255,255,255,0.06)'}` }}>
              <StatCardGrid columns={5} gap={2}>
                <StatCard
                  title={t('reportStops')}
                  value={stats.stopCount}
                  icon={<PauseCircleIcon />}
                  color="primary"
                  size="sm"
                  subtitle={`${stats.uniqueLocations} ${t('sharedLocations')}`}
                />
                <StatCard
                  title={t('sharedTotal')}
                  value={`${stats.totalDuration} h`}
                  icon={<TimerIcon />}
                  size="sm"
                />
                <StatCard
                  title={t('sharedAverage')}
                  value={`${stats.avgDuration} min`}
                  icon={<AvTimerIcon />}
                  color="info"
                  size="sm"
                />
                {parseFloat(stats.totalEngineHours) > 0 && (
                  <StatCard
                    title={t('reportEngineHours')}
                    value={`${stats.totalEngineHours} h`}
                    icon={<LocalGasStationIcon />}
                    color="warning"
                    size="sm"
                  />
                )}
                {stats.longStops > 0 && (
                  <StatCard
                    title="> 1 hora"
                    value={stats.longStops}
                    icon={<WarningIcon />}
                    color="error"
                    size="sm"
                  />
                )}
              </StatCardGrid>

              {/* Chart Toggle */}
              {chartData.length > 1 && (
                <Box sx={{ mt: 2 }}>
                  <Box
                    onClick={() => setShowChart(!showChart)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      cursor: 'pointer',
                      color: tokens?.text?.secondary || '#8b949e',
                      '&:hover': { color: tokens?.accent?.primary || '#00d4aa' },
                    }}
                  >
                    <TimelineIcon fontSize="small" />
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {t('reportChart') || 'Gráfico'}
                    </Typography>
                    {showChart ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                  </Box>
                  <Collapse in={showChart}>
                    <Box
                      sx={{
                        mt: 2,
                        p: 2,
                        backgroundColor: tokens?.surface?.elevated || '#161b22',
                        borderRadius: tokens?.radius?.lg || '12px',
                        border: `1px solid ${tokens?.border?.subtle || 'rgba(255,255,255,0.06)'}`,
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{ mb: 2, color: tokens?.text?.secondary || '#8b949e' }}
                      >
                        {t('reportStops')} {t('reportDaily') || 'por dia'}
                      </Typography>
                      <PremiumBarChart
                        data={chartData}
                        xKey="date"
                        bars={[
                          { dataKey: 'stops', name: t('reportStops'), color: tokens?.accent?.primary || '#00d4aa' },
                        ]}
                        height={200}
                        showLegend={false}
                        tooltipFormatter={(value) => `${value} ${t('reportStops')}`}
                      />
                    </Box>
                  </Collapse>
                </Box>
              )}
            </Box>
          )}
          <Table id="stop-report-table">
            <TableHead>
              <TableRow>
                <TableCell className={classes.columnAction} />
                <TableCell>{t('sharedDevice')}</TableCell>
                {/* 2ECOMP: Mini-map column */}
                <TableCell sx={{ width: 100, textAlign: 'center' }}>{t('positionMap') || 'Mapa'}</TableCell>
                {columns.map((key) => (
                  <TableCell key={key}>{t(columnsMap.get(key))}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {!loading ? (
                items.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell className={classes.columnAction} padding="none">
                      {selectedItem === item ? (
                        <IconButton size="small" onClick={() => { setSelectedItem(null); setShowCard(false); }}>
                          <GpsFixedIcon fontSize="small" />
                        </IconButton>
                      ) : (
                        <IconButton size="small" onClick={() => { setSelectedItem(item); setShowCard(false); }}>
                          <LocationSearchingIcon fontSize="small" />
                        </IconButton>
                      )}
                    </TableCell>
                    <TableCell>{devices[item.deviceId]?.name || item.deviceId}</TableCell>
                    {/* 2ECOMP: Mini-map cell */}
                    <TableCell sx={{ textAlign: 'center', py: 0.5 }}>
                      <MiniMapCell
                        latitude={item.latitude}
                        longitude={item.longitude}
                        size="xs"
                        markerIcon="stop"
                        onClick={() => setExpandedMap({
                          latitude: item.latitude,
                          longitude: item.longitude,
                          title: t('reportStops'),
                          subtitle: formatTime(item.startTime, 'minutes'),
                          deviceName: devices[item.deviceId]?.name,
                        })}
                        tooltipTitle={t('mapExpand') || 'Expandir mapa'}
                      />
                    </TableCell>
                    {columns.map((key) => (
                      <TableCell key={key}>{formatValue(item, key)}</TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableShimmer columns={columns.length + 2} startAction />
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* 2ECOMP: Expanded map modal */}
      <MapExpandModal
        open={!!expandedMap}
        onClose={() => setExpandedMap(null)}
        latitude={expandedMap?.latitude}
        longitude={expandedMap?.longitude}
        title={expandedMap?.title}
        subtitle={expandedMap?.subtitle}
        deviceName={expandedMap?.deviceName}
        markerCategory="stop"
      />
    </PageLayout>
  );
};

export default StopReportPage;
